import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import { ordneMenue, type MenueGruppe } from '@schnitt/shared';
import { Icon } from './Icon';

export interface MenuItem {
  icon: string;
  label: string;
  onSelect: () => void;
  danger?: boolean;
  /** Wo der Eintrag steht (7.70): oben als Zeichen, als Zeile nach Nähe, oder hinter „Mehr“. */
  gruppe?: MenueGruppe;
}

/**
 * A small menu where the finger is. It is placed after it has been measured,
 * so it never hangs off the edge of a phone, and the backdrop ignores taps
 * for a moment after opening: on iOS the release that opened the menu can
 * otherwise land as a click on the backdrop and close it at once.
 */
export function ContextMenu({
  x,
  y,
  items,
  onClose,
  align = 'finger',
}: {
  x: number;
  y: number;
  items: MenuItem[];
  onClose: () => void;
  /** `finger`: centred above the point, for a held clip. `right`: hanging below it, right edge at x -- a menu bar's drop-down. */
  align?: 'finger' | 'right';
}): ReactElement {
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y, ready: false });
  const openedAt = useRef(performance.now());
  // „Mehr“ klappt auf, ohne das Menü zu schliessen -- und danach wird neu
  // gemessen, damit das längere Menü nicht unten hinausragt.
  const [mehrOffen, setMehrOffen] = useState(false);
  const { schnell, zeilen, mehr } = ordneMenue(items);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const pad = 8;
    // Nach unten *und* nach oben klemmen (7.104): ist das Menü höher als der
    // Schirm, ergibt `innerHeight - h - pad` eine negative Zahl -- dann stand
    // sein Kopf über dem Bildrand und das Ende ragte trotzdem hinaus. Die
    // Höhe begrenzt das Stilblatt, hier wird nur noch der Ort geklemmt.
    const obenBleiben = (t: number) => Math.max(pad, Math.min(window.innerHeight - h - pad, t));
    if (align === 'right') {
      setPos({ left: Math.max(pad, Math.min(window.innerWidth - w - pad, x - w)), top: obenBleiben(y), ready: true });
      return;
    }
    const left = Math.max(pad, Math.min(window.innerWidth - w - pad, x - w / 2));
    // Above the finger when there is room, so the hand does not cover it.
    const top = y - h - 14 >= pad ? y - h - 14 : obenBleiben(y + 14);
    setPos({ left, top, ready: true });
  }, [x, y, align, mehrOffen]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [onClose]);

  const zeile = (item: MenuItem): ReactElement => (
    <button
      key={item.label}
      role="menuitem"
      className={item.danger ? 'danger' : ''}
      onClick={() => {
        onClose();
        item.onSelect();
      }}
    >
      <Icon name={item.icon} size={18} />
      <span>{item.label}</span>
    </button>
  );

  const guarded = () => {
    if (performance.now() - openedAt.current < 350) return;
    onClose();
  };

  return (
    <>
      <div className="menu-backdrop" onClick={guarded} onContextMenu={(e) => e.preventDefault()} />
      <div
        ref={box}
        className="context-menu"
        role="menu"
        style={{ left: pos.left, top: pos.top, visibility: pos.ready ? 'visible' : 'hidden' }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {schnell.length > 0 ? (
          <div className="menu-schnell" role="group" aria-label="Schnell">
            {schnell.map((item) => (
              <button
                key={item.label}
                role="menuitem"
                className={item.danger ? 'danger' : ''}
                aria-label={item.label}
                title={item.label}
                onClick={() => {
                  onClose();
                  item.onSelect();
                }}
              >
                <Icon name={item.icon} size={20} />
                <span className="nur-vorlesen">{item.label}</span>
              </button>
            ))}
          </div>
        ) : null}
        {zeilen.map((gruppe, i) => (
          <div key={i} className="menu-gruppe">
            {gruppe.map((item) => zeile(item))}
          </div>
        ))}
        {mehr.length > 0 && !mehrOffen ? (
          <button className="menu-mehr" aria-expanded={false} onClick={() => setMehrOffen(true)}>
            <Icon name="plus" size={18} />
            <span>Mehr</span>
          </button>
        ) : null}
        {mehr.length > 0 && mehrOffen ? <div className="menu-gruppe menu-mehr-offen">{mehr.map((item) => zeile(item))}</div> : null}
      </div>
    </>
  );
}
